/* eslint-disable @typescript-eslint/naming-convention */
import {
  Component,
  ElementRef,
  AfterViewInit,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectorRef,
  TemplateRef,
  ViewContainerRef,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { MatLegacyAutocomplete as MatAutocomplete } from '@angular/material/legacy-autocomplete';
import { UntypedFormControl } from '@angular/forms';
import { ENTER } from '@angular/cdk/keycodes';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { MatSort } from '@angular/material/sort';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { filter, noop } from 'rxjs';

import { ProjectAddContactComponent } from 'app/modules/projects/modals/project-add-contact/project-add-contact.component';
import { ProjectAddLoanRequestComponent } from '../modals/add-loan-request/add-loan-request.component';
import { LoanRequest, LoanQuote } from '../loans.types';
import { AccountService } from 'app/core';
import { ContactsService } from 'app/modules/contacts/contacts.service';
import { LoansService } from 'app/modules/projects/details/loans/loans.service';
import { downloadBlobAs, getAllowedFields, objectToCSV } from 'app/shared/functions';
import { FieldDef, LoanPurpose, SavedView } from 'app/core/models/account.types';
import { FileFormat, RelatedToType } from 'app/shared/enums';
import UrlUtils from 'app/shared/Utils/url.utils';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { FeatureFlag, NewPermission } from '@core/enums';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import { AccountState } from '@state/account/state';

const sortingDataAccessor = (item, property) => {
  switch (property) {
    case 'lender': {
      return item.lender?.first_name;
    }
    case 'lender_company': {
      return item.lender?.company_name;
    }
    default: {
      return item[property];
    }
  }
};

@Component({
  selector: 'project-loan-request-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LoanRequestDetailsComponent extends OnDestroyMixin implements OnInit, OnDestroy, AfterViewInit {
  loanRequest: LoanRequest;
  loanQuotes: LoanQuote[];
  replyFormActive: boolean;
  panelMode: string = 'view';
  selectable = true;
  removable = true;
  separatorKeysCodes: number[] = [ENTER];
  contactList: any[] = [];
  selectedUsers: any[] = [];
  userCtrl: UntypedFormControl;
  visible = true;
  NewPermission = NewPermission;

  @ViewChild('matDrawer', { static: true })
  matDrawer: MatDrawer;

  drawerMode: 'over' | 'side';
  drawerOpened: boolean;

  @ViewChild('auto')
  matAutocomplete: MatAutocomplete;
  @ViewChild('userInput')
  userInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatSort)
  sort: MatSort;

  loanQuotesDataSource: MatTableDataSource<any>;
  loanQuotesTableColumns: string[];
  displayColumns: string[];

  aprFieldDef: any;
  ltvFieldDef: any;

  fieldDefs: FieldDef[] = [];
  filteredFieldDefs: FieldDef[] = [];
  workflowStates: any[] = [];
  loanPurposes: LoanPurpose[];

  @ViewChild('fieldDefsPanelOrigin', { static: false }) private fieldDefsPanelOrigin: MatButton;
  @ViewChild('fieldDefsPanel', { static: false }) private fieldDefsPanel: TemplateRef<any>;

  private fieldDefsPanelOverlayRef: OverlayRef;
  private fieldsDefsPanelTemplatePortal: TemplatePortal;
  private savedView: SavedView;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly loansService: LoansService,
    private readonly matDialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly contactsService: ContactsService,
    private readonly accountService: AccountService,
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly confirmService: ConfirmService,
    private readonly store: Store,
  ) {
    super();

    this.userCtrl = new UntypedFormControl();

    // Set the default
    this.replyFormActive = false;
    this.accountService.currentAccount.pipe(untilComponentDestroyed(this)).subscribe((accountData) => {
      this.contactsService.accountId = accountData.id;
    });

    this.loanQuotesDataSource = new MatTableDataSource();

    this.loanQuotesTableColumns = [
      'workflow_state',
      'lender_company',
      'lender',
      'proceeds',
      'ltv',
      'apr',
      'term',
      'amortization',
      'fees',
      'prepayment',
      'recourse',
      'description',
      'updated_at',
      'delete',
    ];

    this.displayColumns = this.loanQuotesTableColumns.filter((obj) => obj !== 'delete' && obj !== 'lender_edit');

    this.fieldDefs = [
      { name: 'workflow_state', label: 'Status', type: 'text' },
      { name: 'lender_company', label: 'Lender', type: 'text' },
      { name: 'lender', label: 'Lender Name', type: 'text' },
      {
        name: 'proceeds',
        label: 'Proceeds',
        type: 'money',
      },
      {
        name: 'ltv',
        label: 'Leverage',
        type: 'text',
      },
      {
        name: 'apr',
        label: 'Rate',
        type: 'text',
      },
      {
        name: 'term',
        label: 'Term',
        type: 'text',
      },
      {
        name: 'amortization',
        label: 'Amort.',
        type: 'text',
      },
      {
        name: 'fees',
        label: 'Fee',
        type: 'text',
      },
      {
        name: 'prepayment',
        label: 'Prepayment',
        type: 'text',
      },
      {
        name: 'recourse',
        label: 'Recourse',
        type: 'text',
      },
      {
        name: 'description',
        label: 'Notes',
        type: 'textarea',
      },
      {
        name: 'updated_at',
        label: 'Updated At',
        type: 'datetime',
        meta: {
          readonly: true,
        },
      },
      {
        name: 'execution',
        label: 'Execution',
        type: 'text',
      },
      {
        name: 'rate',
        label: 'Rate',
        type: 'text',
      },
      {
        name: 'swap',
        label: 'Swap',
        type: 'checkbox',
      },
    ];
  }

  ngOnInit(): void {
    this.activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.loanPurposes = data.options.loan_purposes;
    });

    this.accountService.fieldDefs$.pipe(untilComponentDestroyed(this)).subscribe((fields: FieldDef[]) => {
      this.handleFieldDefs(fields);
    });

    this.loansService
      .getLoanQuoteStatuses()
      .pipe(untilComponentDestroyed(this))
      .subscribe((loanStatuses) => {
        this.workflowStates = loanStatuses;
      });

    this.loansService.loanRequest$.pipe(untilComponentDestroyed(this)).subscribe((loanRequest: LoanRequest) => {
      if (loanRequest) {
        this.loanRequest = loanRequest;
        this.changeDetectorRef.markForCheck();
      }
    });

    this.loansService.loanQuotes$.pipe(untilComponentDestroyed(this)).subscribe((loanQuotes: LoanQuote[]) => {
      if (loanQuotes) {
        this.loanQuotes = loanQuotes;
        this.loanQuotesDataSource = new MatTableDataSource();
        this.loanQuotesDataSource.data = this.loanQuotes;
        this.loanQuotesDataSource.sort = this.sort;
        this.loanQuotesDataSource.sortingDataAccessor = sortingDataAccessor;
        this.changeDetectorRef.markForCheck();
      }
    });

    this.loansService.selectedLoanRequestChange.pipe(untilComponentDestroyed(this)).subscribe(noop);

    this.accountService.savedViews$.pipe(untilComponentDestroyed(this)).subscribe((savedViews: SavedView[]) => {
      if (savedViews) {
        const LoanQuoteSavedViews = savedViews
          .filter((sv) => sv.related_to_type == 'LoanQuote')
          .sort((a, b) => a?.created_at - b?.created_at);

        let savedViewId = UrlUtils.getFromLocalStorage('loanQuoteSavedView', false);
        if (savedViewId) {
          const foundView = LoanQuoteSavedViews.find((sv) => sv.id == savedViewId);
          if (foundView) {
            this.savedView = foundView;
          } else {
            this.savedView = LoanQuoteSavedViews?.[0];
          }
        } else {
          this.savedView = LoanQuoteSavedViews?.[0];
        }

        if (this.savedView) UrlUtils.saveToLocalStorage('loanQuoteSavedView', this.savedView?.id, false);
        this.renderSavedView();
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  ngAfterViewInit() {
    this.loanQuotesDataSource.sort = this.sort;
    this.loanQuotesDataSource.sortingDataAccessor = sortingDataAccessor;
  }

  ngOnDestroy(): void {
    if (this.fieldDefsPanelOverlayRef) {
      this.fieldDefsPanelOverlayRef.dispose();
    }

    super.ngOnDestroy();
  }

  private handleFieldDefs(fields: FieldDef[]): void {
    if (fields) {
      const customFields = getAllowedFields(fields, RelatedToType.LoanQuote);
      this.fieldDefs.push(...(customFields || []));
      this.filteredFieldDefs = this.fieldDefs;
    }
  }

  editLoanRequest() {
    this.matDialog.open(ProjectAddLoanRequestComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { loanRequest: this.loanRequest, loanPurposes: this.loanPurposes },
    });
  }

  lookupField(name: string): FieldDef | undefined {
    return this.fieldDefs.find((fieldDef) => fieldDef.name === name);
  }

  tableFields() {
    return this.loanQuotesTableColumns.filter((obj) => obj !== 'lender');
  }

  onUpdateLoanQuote($event, loanQuote, skipUpdate = false) {
    let lq = Object.assign(loanQuote, $event);
    this.loansService
      .updateLoanRequote(this.loanRequest.id, lq, skipUpdate)
      .pipe(untilComponentDestroyed(this))
      .subscribe(noop);
  }

  updateLoanQuote(loanQuote): void {
    this.loansService
      .updateLoanRequote(this.loanRequest.id, loanQuote)
      .pipe(untilComponentDestroyed(this))
      .subscribe(noop);
  }

  openContactModal(loanQuote): void {
    if (!this.canEditLoanQuote()) {
      return;
    }

    const dialogRef = this.matDialog.open(ProjectAddContactComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { contactId: loanQuote.lender_id, byPass: true },
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result) {
          this.onUpdateLoanQuote({ lender_id: result.contact_id }, loanQuote);
        }
      });
  }

  deleteLoanRequest(): void {
    this.confirmService
      .openConfirmationModal({
        message: 'Are you sure that you want to delete the entire loan request and all of the quotes?',
      })
      .pipe(filter((res: boolean) => res))
      .subscribe(() => {
        this.loansService.deleteLoanRequest(this.loanRequest.id).pipe(untilComponentDestroyed(this)).subscribe(noop);
      });
  }

  addLoanQuote() {
    let loanQuote = {
      workflow_state: 'sent',
    };

    this.loansService
      .createLoanQuote(this.loanRequest.id, loanQuote)
      .pipe(untilComponentDestroyed(this))
      .subscribe(noop);
  }

  changeEventPanelMode(panelMode: string): void {
    // Set the panel mode
    this.panelMode = panelMode;
  }

  trackByFn(index: number, item: any): any {
    return item?.id || index;
  }

  export(): void {
    const loanQuotesToExport = this.loanQuotes.map((loanQuote) => {
      return {
        Status: loanQuote.workflow_state?.toUpperCase(),
        Lender: loanQuote.lender?.company_name,
        Name: loanQuote.lender?.name,
        Proceeds: loanQuote.proceeds,
        Leverage: loanQuote.ltv,
        Rate: loanQuote.apr,
        Term: loanQuote.term,
        'Amort.': loanQuote.amortization,
        Fee: loanQuote.fees,
        Prepayment: loanQuote.prepayment,
        Recourse: loanQuote.recourse,
        Notes: loanQuote.description,
        'Updated At': loanQuote.updated_at,
      };
    });

    const [firstLoanQuoteToExport] = loanQuotesToExport;
    const columns = Object.keys(firstLoanQuoteToExport);
    const plainCsv = objectToCSV(loanQuotesToExport, columns);
    const fileName = `${this.loanRequest.description.replace(/ /g, '').substring(0, 10)}-${this.loanRequest.id}`;

    downloadBlobAs(fileName, FileFormat.CSV, ['\ufeff' + plainCsv]);
  }

  openFieldDefsPanel(): void {
    this.fieldDefsPanelOverlayRef = this.overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.fieldDefsPanelOrigin._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    this.fieldDefsPanelOverlayRef
      .attachments()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.fieldDefsPanelOverlayRef.overlayElement.querySelector('input').focus();
      });

    this.fieldsDefsPanelTemplatePortal = new TemplatePortal(this.fieldDefsPanel, this.viewContainerRef);

    this.fieldDefsPanelOverlayRef.attach(this.fieldsDefsPanelTemplatePortal);

    this.fieldDefsPanelOverlayRef
      .backdropClick()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.closeDisplayFieldsPanel();
      });
  }

  private closeDisplayFieldsPanel(): void {
    if (this.fieldDefsPanelOverlayRef?.hasAttached()) {
      this.fieldDefsPanelOverlayRef.detach();
      this.filteredFieldDefs = this.fieldDefs;
    }

    if (this.fieldsDefsPanelTemplatePortal?.isAttached) {
      this.fieldsDefsPanelTemplatePortal.detach();
    }
  }

  filterFields(event: KeyboardEvent): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredFieldDefs = this.fieldDefs.filter((fieldDef) => fieldDef.label.toLowerCase().includes(value));
  }

  filterFieldsInputKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      return;
    }

    const fieldDef = this.filteredFieldDefs[0];
    const isFieldVisible = this.fieldDefs.find((item) => item.name === fieldDef.name);

    if (isFieldVisible) {
      this.removeDisplayColumn(fieldDef.name);
    } else {
      this.addDisplayColumn(fieldDef.name);
    }
  }

  private handleLoanQuotesTableColumns(editableColumns: string[]): void {
    this.loanQuotesTableColumns = [...editableColumns, 'delete'];
  }

  toggleDisplayColumn(fieldName: string, change: MatCheckboxChange): void {
    if (change.checked) {
      this.addDisplayColumn(fieldName);
    } else {
      this.removeDisplayColumn(fieldName);
    }
  }

  private addDisplayColumn(fieldName: string): void {
    if (!this.displayColumns.find((item) => item === fieldName)) {
      this.displayColumns.unshift(fieldName);
      this.handleLoanQuotesTableColumns(this.displayColumns);
      this.saveColumns();
      this.changeDetectorRef.markForCheck();
    }
  }

  private removeDisplayColumn(fieldName: string): void {
    this.displayColumns.splice(
      this.displayColumns.findIndex((item) => item === fieldName),
      1,
    );
    this.loanQuotesTableColumns.splice(
      this.loanQuotesTableColumns.findIndex((item) => item === fieldName),
      1,
    );
    this.saveColumns();
    this.changeDetectorRef.markForCheck();
  }

  private renderSavedView(): void {
    if (this.savedView?.definition) {
      this.displayColumns = JSON.parse(this.savedView.definition)?.table?.fields;
      this.displayColumns = this.displayColumns.filter((column) =>
        this.fieldDefs.find((field) => field.name === column),
      );
      this.handleLoanQuotesTableColumns(this.displayColumns);
    }
  }

  private saveColumns(): void {
    if (!this.savedView) {
      this.savedView = {
        id: null,
        name: 'Loan Quotes',
        related_to_type: RelatedToType.LoanQuote,
        definition: JSON.stringify({ table: { fields: this.displayColumns } }),
      };
      this.accountService.createSavedView(this.savedView).pipe(untilComponentDestroyed(this)).subscribe(noop);
    } else {
      this.savedView.definition = JSON.stringify({ table: { fields: this.displayColumns } });
      this.accountService.updateSavedView(this.savedView).pipe(untilComponentDestroyed(this)).subscribe(noop);
    }
  }

  dropColumns(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.displayColumns, event.previousIndex, event.currentIndex);
      this.saveColumns();
    }
  }

  canEditLoanQuote(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.LOAN_QUOTES_EDIT));
    }

    return true;
  }
}
